import { z } from "zod";

export const AUTH_METHOD_SSO_PROVIDER_ID_KEY = "auth-method-sso-provider-id";
export const SSO_ERROR_PATH = "/sso-error";
const SSO_ATTEMPT_STORAGE_KEY = "auth-sso-attempt";
// Matches better-auth's OAuth state cookie lifetime: the identity provider flow can complete until then.
const SSO_ATTEMPT_MAX_AGE_MS = 10 * 60 * 1000;

const SSOAttemptSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  startedAt: z.iso.datetime(),
});

export type SSOAttempt = z.infer<typeof SSOAttemptSchema>;

function removeStoredSSOAttempt(): void {
  try {
    window.localStorage.removeItem(SSO_ATTEMPT_STORAGE_KEY);
  } catch {
    // pass
  }
}

function readActiveSSOAttempt(): SSOAttempt | undefined {
  try {
    const storedAttempt = window.localStorage.getItem(SSO_ATTEMPT_STORAGE_KEY);
    if (!storedAttempt) {
      return undefined;
    }

    const parsedAttempt = SSOAttemptSchema.safeParse(JSON.parse(storedAttempt));
    if (!parsedAttempt.success || Date.now() - Date.parse(parsedAttempt.data.startedAt) > SSO_ATTEMPT_MAX_AGE_MS) {
      removeStoredSSOAttempt();
      return undefined;
    }
    return parsedAttempt.data;
  } catch {
    removeStoredSSOAttempt();
    return undefined;
  }
}

export function startSSOAttempt(providerId: string): {
  attempt: SSOAttempt;
  previousAttempt: SSOAttempt | undefined;
} {
  const previousAttempt = readActiveSSOAttempt();
  const attempt = { id: window.crypto.randomUUID(), providerId, startedAt: new Date().toISOString() };
  try {
    window.localStorage.setItem(SSO_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // pass
  }
  return { attempt, previousAttempt };
}

export function hasActiveSSOAttempt(providerId: string): boolean {
  return readActiveSSOAttempt()?.providerId === providerId;
}

export function clearSSOAttempt(attemptId: string): void {
  const activeAttempt = readActiveSSOAttempt();
  if (activeAttempt?.id === attemptId) {
    removeStoredSSOAttempt();
  }
}

export function clearActiveSSOAttempt(): void {
  removeStoredSSOAttempt();
}

export function clearSSOState(): void {
  try {
    window.localStorage.removeItem(AUTH_METHOD_SSO_PROVIDER_ID_KEY);
    window.localStorage.removeItem(SSO_ATTEMPT_STORAGE_KEY);
  } catch {
    //pass
  }
}
